import { useCallback } from 'react'

import { usePublicClient } from 'wagmi'
import { TransactionReceipt } from 'viem'

import { Hash } from 'types/entities'

import { Transaction } from 'state/user/types'

import { sleep } from 'utils/helpers'

import { errors } from 'constants/errors'
import { TxStatuses } from 'constants/transaction'

const TIME_OUT_ERROR = 'Re-fetch transaction receipt'
export function useWaitTx() {
  const { waitForTransactionReceipt, getBlock } = usePublicClient()

  const waitForTx = useCallback(
    async (hash: Hash): Promise<TransactionReceipt> => {
      try {
        const result = await Promise.race<TransactionReceipt>([
          waitForTransactionReceipt({ hash }),
          new Promise(function (resolve, reject) {
            setTimeout(() => {
              reject(new Error(TIME_OUT_ERROR))
            }, 20000)
          }),
        ])

        return result
      } catch (err) {
        if (err.message === TIME_OUT_ERROR) {
          return await waitForTx(hash)
        }
        throw err
      }
    },
    [waitForTransactionReceipt],
  )

  const receiptWaiter = useCallback(
    async (tx: Omit<Transaction, 'status'> & { status?: TxStatuses }, retryAttempt = 0): Promise<Transaction> => {
      try {
        if (!tx.status) {
          return { ...tx, status: TxStatuses.PENDING }
        }

        const receipt = await waitForTx(tx.txHash)

        if (receipt.status === 'reverted') {
          throw new Error(errors.transaction.TX_FAIL)
        }

        const { timestamp } = await getBlock({ blockNumber: receipt.blockNumber })

        return { ...tx, timestamp: timestamp.toString(), status: TxStatuses.SUCCESS }
      } catch (err) {
        if (retryAttempt === 4) {
          return { ...tx, status: TxStatuses.FAIL, error: err.message }
        }
        await sleep(1500)
        return receiptWaiter(tx, retryAttempt + 1)
      }
    },
    [waitForTransactionReceipt],
  )

  return receiptWaiter
}
